import { useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import type { Project } from '@/types/content';
import { FadeIn } from '@/components/motion/FadeIn';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import { deriveVariants } from '@/lib/media';
import { getMaterialSwatch } from '@/lib/materials';

gsap.registerPlugin(ScrollTrigger, useGSAP);

interface ProjectScrollNarrativeProps {
  project: Project;
}

/**
 * Project detail page body. Hero with subtle scroll-driven Ken Burns
 * (slow scale + slight parallax) on the hero image, then the MDX body,
 * a materials list, and a gallery grid.
 */
export function ProjectScrollNarrative({ project }: ProjectScrollNarrativeProps) {
  const heroVariants = deriveVariants(project.heroImage);
  const Body = project.Body;
  const heroRef = useRef<HTMLElement>(null);
  const heroImgRef = useRef<HTMLImageElement>(null);
  const reducedMotion = useReducedMotion();

  useGSAP(
    () => {
      if (reducedMotion || !heroRef.current || !heroImgRef.current) return;
      const trigger = gsap.fromTo(
        heroImgRef.current,
        { scale: 1.02, y: 0 },
        {
          scale: 1.16,
          y: '8%',
          ease: 'none',
          scrollTrigger: {
            trigger: heroRef.current,
            start: 'top top',
            end: 'bottom top',
            scrub: true,
          },
        }
      );
      return () => {
        trigger.scrollTrigger?.kill();
        trigger.kill();
      };
    },
    { scope: heroRef, dependencies: [reducedMotion] }
  );

  return (
    <article className="project-narrative">
      <header ref={heroRef} className="project-narrative__hero">
        <picture className="project-narrative__hero-img">
          {heroVariants.avif ? <source srcSet={heroVariants.avif} type="image/avif" /> : null}
          {heroVariants.webp ? <source srcSet={heroVariants.webp} type="image/webp" /> : null}
          <img
            ref={heroImgRef}
            src={heroVariants.fallback}
            alt={project.heroAlt}
            loading="eager"
            decoding="async"
            fetchPriority="high"
          />
        </picture>
        <div className="container project-narrative__hero-copy">
          <FadeIn>
            <p className="eyebrow">{project.typology} · {project.location} · {project.year}</p>
            <h1 className="project-narrative__title">{project.title}</h1>
            <p className="subtitle">{project.summary}</p>
          </FadeIn>
        </div>
      </header>

      <div className="container project-narrative__body">
        <Body />
      </div>

      {project.materials && project.materials.length > 0 ? (
        <>
          <p className="ornament container" aria-hidden="true">§</p>
          <section className="project-narrative__materials container">
            <FadeIn>
              <p className="eyebrow">Materials</p>
              <ul className="project-narrative__material-list">
                {project.materials.map((m) => {
                  const swatch = getMaterialSwatch(m);
                  return (
                    <li key={m} className="project-narrative__material">
                      <span
                        className="project-narrative__swatch"
                        style={{
                          background: `radial-gradient(circle at 30% 30%, ${swatch.base}, ${swatch.shadow})`,
                        }}
                        aria-hidden="true"
                      />
                      <span className="display-italic project-narrative__material-name">{m}</span>
                    </li>
                  );
                })}
              </ul>
            </FadeIn>
          </section>
        </>
      ) : null}

      {project.gallery && project.gallery.length > 0 ? (
        <section className="project-narrative__gallery container">
          <FadeIn>
            <p className="eyebrow project-narrative__gallery-label">Gallery</p>
          </FadeIn>
          {project.gallery.map((img, i) => {
            const v = deriveVariants(img.src);
            // First gallery image spans full width; alternates 2-column after that
            const className = i === 0 ? 'project-narrative__gallery-item project-narrative__gallery-item--wide' : 'project-narrative__gallery-item';
            return (
              <FadeIn key={i} delay={i * 80}>
                <figure className={className}>
                  <picture>
                    {v.avif ? <source srcSet={v.avif} type="image/avif" /> : null}
                    {v.webp ? <source srcSet={v.webp} type="image/webp" /> : null}
                    <img src={v.fallback} alt={img.alt} loading="lazy" decoding="async" />
                  </picture>
                  {img.caption ? <figcaption className="caption">{img.caption}</figcaption> : null}
                </figure>
              </FadeIn>
            );
          })}
        </section>
      ) : null}
    </article>
  );
}
